// ColonyResourceController — central colony-wide load-shedding + production control
// Implements: power emergency detection, 5 alert levels, hysteresis, storm prediction,
// production demand logic, storage reserve protection, construction yard logic,
// colony policies, and gradual restart.

import { getBuildingDef } from "../buildingSystem";
import { BUILDINGS } from "../data/buildings";

// EM2c resource names → game resource names
const EM2C_RES_MAP = {
  power: "power", raw_water: "water", clean_water: "water", water: "water",
  oxygen: "oxygen", food: "food", raw_food: "food", processed_food: "food",
  metal: "metal", metal_ore: "metal", refined_metal: "metal", sorted_ore: "metal",
  parts: "parts", machine_parts: "parts", polymers: "polymers", credits: "credits",
};

// ─── Building Priority Classification ───
// P0 = Emergency (never shed), P1 = Life Critical (last resort), P2 = Colony Essential,
// P3 = Industrial Essential, P4 = Non-Essential Production, P5 = Luxury (first to go)

const CATEGORY_PRIORITY = {
  core: 0,           // command centers — never shut down
  power: 0,          // power generation — never shed the source
  life: 1,           // life support (oxygen, water, atmo)
  habitation: 1,     // habitats
  medical: 1,         // medical
  storage: 0,        // storage is infrastructure
  drones: 2,         // drone centers — colony essential
  communications: 2, // relay towers
  spaceport: 2,      // cargo landing
  mining: 3,         // industrial essential
  manufacturing: 4,  // non-essential production (except construction_yard which gets P4)
  research: 4,       // non-essential
  expansion: 5,     // luxury / optional
};

// Specific building overrides
const BUILDING_PRIORITY_OVERRIDE = {
  command_center: 0,
  command: 0,
  solar: 0,
  power_reactor: 0,
  power_distributor: 0,
  radiator_field: 2,
  construction_yard: 4,
  cold_storage: 1,
  relay_tower: 2,
  cargo_landing: 2,
  drone_center: 2,
  vehicle_depot: 3,
  component_factory: 3,
  ore_refinery: 3,
  mining_processor: 3,
  regolith_processor: 3,
  print_factory: 4,
  agri_complex: 2,
  closed_loop: 2,
};

export function getBuildingPriority(buildingType) {
  if (BUILDING_PRIORITY_OVERRIDE[buildingType] !== undefined) {
    return BUILDING_PRIORITY_OVERRIDE[buildingType];
  }
  const def = getBuildingDef(buildingType) || BUILDINGS[buildingType];
  if (!def) return 4;
  return CATEGORY_PRIORITY[def.category] ?? 4;
}

// ─── Alert Levels ───
export const ALERT_LEVELS = {
  NORMAL:       { id: "NORMAL",       label: "Normal",       color: "#00fa9a", priority: 0 },
  CONSERVATION: { id: "CONSERVATION", label: "Conservation",  color: "#7fff7f", priority: 1 },
  WARNING:      { id: "WARNING",      label: "Power Warning", color: "#ffc107", priority: 2 },
  EMERGENCY:    { id: "EMERGENCY",    label: "Emergency",     color: "#ff8c00", priority: 3 },
  SURVIVAL:     { id: "SURVIVAL",     label: "Survival",      color: "#ff3366", priority: 4 },
};

const ALERT_SHED_MAP = {
  NORMAL:       5,
  CONSERVATION: 4,
  WARNING:      3,
  EMERGENCY:    2,
  SURVIVAL:     1,
};

// ─── Colony Policies ───
export const COLONY_POLICIES = {
  SURVIVAL:       { id: "SURVIVAL",       label: "Survival",       batteryConservation: 70, batteryWarning: 50, batteryEmergency: 35, batterySurvival: 20, reserveConservation: 24, reserveWarning: 14, reserveEmergency: 7, reserveSurvival: 3 },
  BALANCED:       { id: "BALANCED",       label: "Balanced",       batteryConservation: 60, batteryWarning: 40, batteryEmergency: 25, batterySurvival: 10, reserveConservation: 18, reserveWarning: 10, reserveEmergency: 5, reserveSurvival: 2 },
  MAX_PRODUCTION: { id: "MAX_PRODUCTION", label: "Max Production", batteryConservation: 45, batteryWarning: 30, batteryEmergency: 18, batterySurvival: 8,  reserveConservation: 12, reserveWarning: 6,  reserveEmergency: 3, reserveSurvival: 1.5 },
};

// ─── Resource Reserve Targets ───
const RESERVE_TARGETS = {
  oxygen:   { min: 50, target: 150, max: 300, protected: 0.90 },
  water:    { min: 50, target: 150, max: 300, protected: 0.85 },
  food:     { min: 40, target: 120, max: 240, protected: 0.75 },
  metal:    { min: 20, target: 100, max: 300, protected: 0.50 },
  parts:    { min: 20, target: 80,  max: 160, protected: 0.50 },
  polymers: { min: 10, target: 60,  max: 120, protected: 0.40 },
  credits:  { min: 0,  target: 200, max: 999, protected: 0.0  },
  power:    { min: 10, target: 100, max: 500, protected: 0.0  },
};

const HYSTERESIS_RECOVERY_TIME = 300; // 5 min real-time

function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

// ─── Main controller ───
export function runColonyController(state, dt) {
  const { resources, buildings, storm, colonyPolicy, colonists } = state;
  const policy = COLONY_POLICIES[colonyPolicy] || COLONY_POLICIES.BALANCED;

  // 1. Power production & consumption
  let powerProduction = 0;
  let powerConsumption = 0;
  const daylight = clamp(Math.sin(state.time * Math.PI) * 1.4, 0, 1);

  for (const b of buildings) {
    if (b.progress < 1) continue;
    const def = getBuildingDef(b.type) || BUILDINGS[b.type];
    if (!def) continue;
    if (_isShed(b, state)) continue;

    // EM2c: productionOutputs.power; legacy: def.power (positive = generator)
    let genRate = (def.productionOutputs?.power) || (def.powerGeneration) || (def.power > 0 ? def.power : 0) || 0;
    if (genRate > 0) {
      const isSolar = b.type === "solar" || b.type === "solar_station" || b.type === "solar_panel";
      let mult = isSolar
        ? daylight * (storm.active ? (state.research?.completed?.includes("aero_storm") ? 0.5 : 0.15) : 1) * (state.research?.completed?.includes("eng_solar2") ? 1.5 : 1) * b.condition
        : b.condition;
      powerProduction += genRate * mult;
    }

    // EM2c: powerConsumption field; legacy: def.power (negative = consumer)
    let conRate = def.powerConsumption || (def.power < 0 ? -def.power : 0);
    if (conRate > 0) powerConsumption += conRate * b.condition;
  }

  // 2. Battery state
  const battery = resources.power || { stored: 0, max: 200 };
  const batteryPct = battery.max > 0 ? (battery.stored / battery.max) * 100 : 100;
  const netPower = powerProduction - powerConsumption;
  const batteryDrainRate = Math.max(0, -netPower);
  const batteryReserveHours = batteryDrainRate > 0
    ? (battery.stored / batteryDrainRate) / 3600 * 100
    : null;

  // 3. Storm prediction
  let stormIncoming = false;
  let stormHoursAway = null;
  let stormEnergyDeficit = 0;

  if (storm.warning && !storm.active) {
    stormIncoming = true;
    stormHoursAway = Math.max(0.5, storm.next * 8);
    const stormDuration = 0.4 * 8;

    let stormGen = 0;
    for (const b of buildings) {
      if (b.progress < 1) continue;
      const def = getBuildingDef(b.type) || BUILDINGS[b.type];
      if (!def) continue;
      const genRate2 = (def.productionOutputs?.power) || (def.powerGeneration) || (def.power > 0 ? def.power : 0) || 0;
      if (genRate2 > 0) {
        const isSolar2 = b.type === "solar" || b.type === "solar_station" || b.type === "solar_panel";
        stormGen += genRate2 * (isSolar2 ? 0.1 : 1) * b.condition;
      }
    }

    const expectedStormEnergy = battery.stored + stormGen * stormDuration * 36;
    const expectedStormDemand = powerConsumption * stormDuration * 36;
    stormEnergyDeficit = Math.max(0, expectedStormDemand - expectedStormEnergy);
  }

  // 4. Alert level with hysteresis
  const prevAlert = state._colonyAlert || ALERT_LEVELS.NORMAL;
  const alert = _computeAlertLevel(
    batteryPct, batteryReserveHours, stormIncoming, stormEnergyDeficit,
    policy, prevAlert, state._alertStableSince || 0, dt, state._totalTime || 0
  );

  // 5. Determine which buildings to shed
  const maxAllowedPriority = ALERT_SHED_MAP[alert.id];
  const shedBuildings = new Set();
  const buildingStatuses = {};

  for (const b of buildings) {
    if (b.progress < 1) {
      buildingStatuses[b.id] = { status: "constructing", reason: null };
      continue;
    }
    const def = getBuildingDef(b.type) || BUILDINGS[b.type];
    if (!def) continue;

    if (b.condition < 0.3) {
      shedBuildings.add(b.id);
      buildingStatuses[b.id] = { status: "damaged", reason: "Damaged" };
      continue;
    }

    // ── Power producers are NEVER shed by the controller ──
    // They always run. Excess production is naturally capped by storage max in the tick loop.
    const isPowerProducer = (def.productionOutputs?.power > 0) || (def.power > 0) || (def.powerGeneration > 0);
    if (isPowerProducer) {
      buildingStatuses[b.id] = { status: "operational", reason: null };
      continue;
    }

    const priority = state.buildingOverrides?.[b.id] ?? getBuildingPriority(b.type);

    if (alert.priority >= ALERT_LEVELS.EMERGENCY.priority && priority > maxAllowedPriority) {
      shedBuildings.add(b.id);
      buildingStatuses[b.id] = { status: "power_shed", reason: `Power shed (P${priority})` };
      continue;
    }

    if (alert.id === "SURVIVAL" && priority === 2) {
      buildingStatuses[b.id] = { status: "reduced", reason: "Survival mode — 30% capacity", reducedCapacity: 0.3 };
    }

    // Production checks
    const hasInputs = def.productionInputs && Object.keys(def.productionInputs).length > 0;
    const hasOutputs = def.productionOutputs && Object.keys(def.productionOutputs).length > 0;

    if (hasInputs) {
      let inputMissing = false;
      for (const [emKey, rate] of Object.entries(def.productionInputs)) {
        const gameKey = EM2C_RES_MAP[emKey] || emKey;
        if (rate > 0 && (resources[gameKey]?.stored || 0) <= 0.5) {
          inputMissing = true;
          break;
        }
      }
      if (inputMissing) {
        shedBuildings.add(b.id);
        buildingStatuses[b.id] = { status: "no_input", reason: "Input shortage" };
        continue;
      }
    }

    // ── No auto-idle ──
    // Buildings are NEVER auto-shut for "output full" or "demand satisfied".
    // The tick loop naturally caps stored resources at max, so excess production
    // simply doesn't get added. Buildings only stop if:
    //   1. Player manually disables them
    //   2. They're physically damaged (condition < 0.3)
    //   3. They lack required inputs (can't produce without materials)
    //   4. Power emergency shedding (EMERGENCY/SURVIVAL only, non-power buildings)

    // Construction yard idle check
    if (b.type === "construction_yard") {
      const hasActiveConstruction = buildings.some((other) => other.progress < 1 && other.id !== b.id);
      if (!hasActiveConstruction) {
        buildingStatuses[b.id] = { status: "standby", reason: "No construction queued", idle: true };
      } else {
        buildingStatuses[b.id] = { status: "operational", reason: null };
      }
    } else {
      buildingStatuses[b.id] = { status: "operational", reason: null };
    }
  }

  // 6. Count shed by priority
  const shedCount = {};
  for (const b of buildings) {
    if (shedBuildings.has(b.id)) {
      const p = state.buildingOverrides?.[b.id] ?? getBuildingPriority(b.type);
      shedCount[p] = (shedCount[p] || 0) + 1;
    }
  }

  return {
    alert,
    alertId: alert.id,
    alertLabel: alert.label,
    alertColor: alert.color,
    powerProduction,
    powerConsumption,
    netPower,
    batteryPct,
    batteryReserveHours,
    stormIncoming,
    stormHoursAway,
    stormEnergyDeficit,
    shedBuildings,
    buildingStatuses,
    shedCount,
    activeCount: buildings.filter(b => b.progress >= 1 && !shedBuildings.has(b.id)).length,
    totalCount: buildings.filter(b => b.progress >= 1).length,
  };
}

// ─── Alert computation with hysteresis ───
function _computeAlertLevel(batteryPct, reserveHours, stormIncoming, stormDeficit, policy, prevAlert, stableSince, dt, totalTime) {
  const triggerAlert = _getTriggerAlert(batteryPct, reserveHours, stormIncoming, stormDeficit, policy);

  if (triggerAlert.priority > prevAlert.priority) return triggerAlert;
  if (triggerAlert.id === prevAlert.id) return prevAlert;

  // Recovery: need hysteresis
  const recoveryAlert = _getRecoveryAlert(batteryPct, reserveHours, policy);
  if (recoveryAlert.priority <= triggerAlert.priority) {
    const stableTime = totalTime - stableSince;
    if (stableTime >= HYSTERESIS_RECOVERY_TIME) return recoveryAlert;
  }
  return prevAlert;
}

function _getTriggerAlert(batteryPct, reserveHours, stormIncoming, stormDeficit, policy) {
  if (batteryPct < policy.batterySurvival || (reserveHours != null && reserveHours < policy.reserveSurvival))
    return ALERT_LEVELS.SURVIVAL;
  if (batteryPct < policy.batteryEmergency || (reserveHours != null && reserveHours < policy.reserveEmergency))
    return ALERT_LEVELS.EMERGENCY;
  if (batteryPct < policy.batteryWarning || (reserveHours != null && reserveHours < policy.reserveWarning))
    return ALERT_LEVELS.WARNING;
  if (batteryPct < policy.batteryConservation || (reserveHours != null && reserveHours < policy.reserveConservation))
    return ALERT_LEVELS.CONSERVATION;
  if (stormIncoming && stormDeficit > 0)
    return ALERT_LEVELS.CONSERVATION;
  return ALERT_LEVELS.NORMAL;
}

function _getRecoveryAlert(batteryPct, reserveHours, policy) {
  if (batteryPct >= policy.batteryConservation + 15 && (reserveHours == null || reserveHours >= policy.reserveConservation + 6))
    return ALERT_LEVELS.NORMAL;
  if (batteryPct >= policy.batteryWarning + 15 && (reserveHours == null || reserveHours >= policy.reserveWarning + 4))
    return ALERT_LEVELS.CONSERVATION;
  if (batteryPct >= policy.batteryEmergency + 15 && (reserveHours == null || reserveHours >= policy.reserveEmergency + 3))
    return ALERT_LEVELS.WARNING;
  if (batteryPct >= policy.batterySurvival + 10 && (reserveHours == null || reserveHours >= policy.reserveSurvival + 1.5))
    return ALERT_LEVELS.EMERGENCY;
  return ALERT_LEVELS.SURVIVAL;
}

function _isShed(b, state) {
  if (!state._colonyControllerState) return false;
  return state._colonyControllerState.shedBuildings?.has(b.id) || false;
}

// ─── Exports for UI ───
export function getDemandLevel(resourceId, stored, max) {
  const targets = RESERVE_TARGETS[resourceId];
  if (!targets) return "normal";
  if (stored <= targets.min) return "emergency";
  if (stored <= targets.target) return "normal";
  if (stored <= targets.max * 0.9) return "reduced";
  if (stored <= targets.max * 0.97) return "minimal";
  return "stop";
}

export function isResourceProtected(resourceId, stored, max) {
  const targets = RESERVE_TARGETS[resourceId];
  if (!targets || targets.protected === 0) return false;
  const pct = max > 0 ? stored / max : 1;
  return pct < targets.protected;
}

export function getRecoverySequence(currentAlert, targetAlert) {
  const steps = [];
  for (let p = currentAlert.priority - 1; p >= targetAlert.priority; p--) steps.push(p);
  return steps;
}

export function getPriorityLabel(priority) {
  const labels = ["P0 · Emergency", "P1 · Life Critical", "P2 · Colony Essential", "P3 · Industrial", "P4 · Non-Essential", "P5 · Luxury"];
  return labels[priority] || `P${priority}`;
}

export function getPriorityColor(priority) {
  const colors = ["#ff3366", "#ff8c00", "#ffc107", "#7fff7f", "#00e5ff", "#8b5cf6"];
  return colors[priority] || "#666";
}
